import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Box,
  Typography,
  Link,
  CircularProgress,
  Paper,
  Fade,
  InputAdornment,
  IconButton,
} from '@mui/material';
import { 
  Visibility,
  VisibilityOff,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../contexts/ToastContext';
import { apiService } from '../services/api';
import { logger } from '../utils/logger';
import { useBackgroundImage } from '../hooks/useBackgroundImage';
import { BackgroundSelector } from '../components/BackgroundSelector';
import { Logo } from '../components/Logo';
import { useTheme } from '@mui/material/styles';
import { CorporateTextField, CorporateButton } from '../components/ui';
import { isPublicRoute } from '../constants/routes';
import {
  createAuthPaperStyles,
  createBackgroundContainerStyles,
  createGradientTitleStyles,
  createPrimaryActionButtonStyles,
} from '../theme/utils';

export const Login: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const { login, isAuthenticated } = useAuth();
  const { showError, showInfo } = useToast();
  const theme = useTheme();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  
  const { currentBg, backgroundImages, selectedBackground, changeBackground, fallbackGradient, useFallback } = useBackgroundImage();

  useEffect(() => {
    // Solo redirigir si el usuario está autenticado y NO estamos en proceso de login
    // Esto evita redirecciones prematuras durante el proceso de login
    if (isAuthenticated && !loading) {
      const from = (location.state as any)?.from?.pathname;
      // Solo redirigir a 'from' si es una ruta válida (no pública)
      // Si viene de una ruta pública como /privacy-policy, redirigir a ensayos
      const targetPath = (from && !isPublicRoute(from)) ? from : '/ensayos';
      navigate(targetPath, { replace: true });
    }
  }, [isAuthenticated, navigate, location, loading]);

  useEffect(() => {
    const checkFirstAdmin = async () => {
      try {
        const result = await apiService.needsFirstAdmin();
        if (result.needs) {
          showInfo(t('auth.firstAdminInfo'));
        }
      } catch (error) {
        logger.error('Error verificando primer admin:', error);
      }
    };
    checkFirstAdmin();
  }, [showInfo, t]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);

    try {
      // Solo llamar a login() del contexto, que maneja apiService.login() internamente
      // y retorna el usuario para evitar llamadas duplicadas
      const userData = await login(email, password);
      
      // Verificar si se requiere reset de contraseña después del login
      if (userData?.password_reset_required) {
        navigate('/reset-password', { replace: true });
        return;
      }

      // Redirigir a ensayos o a la ruta de origen (si no es pública)
      const from = (location.state as any)?.from?.pathname;
      const targetPath = (from && !isPublicRoute(from)) ? from : '/ensayos';
      navigate(targetPath, { replace: true });
    } catch (err: any) {
      showError(err.response?.data?.detail || t('auth.loginError'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box
      sx={createBackgroundContainerStyles(
        theme,
        currentBg.url,
        useFallback,
        fallbackGradient
      )}
    >
      {/* Selector de fondo */}
      <BackgroundSelector
        selectedBackground={selectedBackground}
        backgroundImages={backgroundImages}
        onBackgroundChange={changeBackground}
        fallbackGradients={{
          '1': 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          '2': 'linear-gradient(135deg, #11998e 0%, #38ef7d 100%)',
          '3': 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)',
        }}
      />

      {/* Formulario de login */}
      <Fade in={true}>
        <Paper
          elevation={24}
          sx={{
            ...createAuthPaperStyles(theme),
            p: 5,
            width: '100%',
            maxWidth: 360,
          }}
        >
          <Box sx={{ textAlign: 'center', mb: 4 }}>
            <Box sx={{ mb: 3, display: 'flex', justifyContent: 'center' }}>
              <Logo variant="auto" width={180} />
            </Box>
            <Typography
              component="h1"
              variant="h3"
              sx={{
                ...createGradientTitleStyles(theme),
                mb: 1,
              }}
            >
              {t('auth.welcome')}
            </Typography>
            <Typography variant="body1" color="text.secondary">
              {t('auth.loginSubtitle')}
            </Typography>
          </Box>

          <Box component="form" onSubmit={handleSubmit}>
            <CorporateTextField
              margin="normal"
              required
              fullWidth
              id="email"
              label={t('common.email')}
              name="email"
              autoComplete="off"
              autoFocus
              value={email}
              onChange={(e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => setEmail(e.target.value)}
              type="email"
              sx={{ mb: 2 }}
            />
            <CorporateTextField
              margin="normal"
              required
              fullWidth
              name="password"
              label={t('common.password')}
              type={showPassword ? 'text' : 'password'}
              id="password"
              autoComplete="new-password"
              value={password}
              onChange={(e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => setPassword(e.target.value)}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      aria-label="toggle password visibility"
                      onClick={() => setShowPassword(!showPassword)}
                      edge="end"
                    >
                      {showPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
              sx={{ mb: 3 }}
            />
            <CorporateButton
              type="submit"
              fullWidth
              variant="contained"
              size="large"
              disabled={loading}
              colorVariant="accent"
              sx={{
                mt: 2,
                mb: 2,
                py: 1.5,
                ...createPrimaryActionButtonStyles(theme),
              }}
            >
              {loading ? <CircularProgress size={24} color="inherit" /> : t('auth.login')}
            </CorporateButton>
            <Box textAlign="center" sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              {/* Enlace de registro eliminado - Gestión centralizada de usuarios */}
              <Link
                component="button"
                variant="body2"
                onClick={() => navigate('/privacy-policy')}
                sx={{
                  cursor: 'pointer',
                  fontWeight: 400,
                  fontSize: '0.7rem',
                  color: 'text.secondary',
                  textDecoration: 'none',
                  '&:hover': {
                    textDecoration: 'underline',
                  },
                }}
              >
                {t('auth.privacyPolicy', 'Política de Privacidad')}
              </Link>
            </Box>
          </Box>
        </Paper>
      </Fade>
    </Box>
  );
};
